// Page publique /pricing — vitrine commerciale HInvoice (Lot 8.2 + 8.5.5c).
//
// Accessible sans authentification. Charge GET /api/plans et affiche les
// plans publics (free / pro / business) avec leurs quotas, features et CTA.
//
// CTA selon contexte :
// - visiteur anonyme               : "Essayer 14 jours" / "Créer un compte" → /signup?plan=<slug>
// - utilisateur connecté non-admin : "Ouvrir HInvoice" → /
// - utilisateur connecté admin sans souscription :
//     plan free   → "Ouvrir HInvoice" → /
//     plan payant → "Souscrire à [Plan]" → POST /api/billing/start + redirect Ziina (Lot 8.5.5c)
// - utilisateur connecté admin avec souscription : "Gérer ma facturation" → /settings/billing
//
// Le plan "Pro" est marqué comme recommandé (badge + bordure accent). Le
// choix est codé en dur ici plutôt que sur un flag `is_recommended` côté
// back parce que c'est une décision marketing qui peut bouger vite et ne
// mérite pas une colonne dédiée au schéma.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "../api/client";
import { billingApi, type BillingMeResponse } from "../api/billing";
import { fetchPublicPlans } from "../api/plans";
import type { PlanFeatures, PlanPublic, UserRole } from "../api/types";
import { selectIsAuthenticated, useAuthStore } from "../stores/auth";

const RECOMMENDED_SLUG = "pro";

// Ordre d'affichage des features dans la carte. Une feature non présente
// côté back sera affichée en "—" (pas de migration requise pour ajouter
// une feature nouvelle : elle apparaît dès qu'elle est seedée).
const FEATURE_LABELS: Array<{ key: keyof PlanFeatures; label: string }> = [
  { key: "ocr", label: "OCR Claude Vision" },
  { key: "reminders", label: "Relances automatiques" },
  { key: "payments", label: "Liens de paiement Ziina" },
  { key: "export", label: "Export CSV / Excel" },
  { key: "api_access", label: "Accès API" },
];

function formatPrice(cents: number, currency: string): string {
  const euros = cents / 100;
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
      maximumFractionDigits: euros % 1 === 0 ? 0 : 2,
    }).format(euros);
  } catch {
    // Devise inconnue / Intl indisponible (tests SSR) — fallback simple.
    return `${euros} ${currency}`;
  }
}

function formatQuota(quota: number): string {
  // Convention back : -1 = illimité. Les plans seedés actuels exposent
  // tous un entier positif, mais on gère -1 pour rester défensif (un
  // plan "Enterprise" manuel pourrait en avoir besoin plus tard).
  if (quota < 0) return "Illimité";
  return `${quota} factures / mois`;
}

function formatSeats(seats: number): string {
  if (seats < 0) return "Utilisateurs illimités";
  if (seats === 1) return "1 utilisateur";
  return `${seats} utilisateurs`;
}

export function PricingPage() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const currentRole = useAuthStore((s) => s.currentRole);

  const [plans, setPlans] = useState<PlanPublic[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Lot 8.5.5c — état de facturation chargé uniquement si on est connecté.
  // Sert à décider quel CTA afficher (souscrire vs gérer vs ouvrir).
  const [billingMe, setBillingMe] = useState<BillingMeResponse | null>(null);

  // État de l'appel /start en cours (un seul à la fois). Stocke le slug
  // du plan en cours de souscription pour désactiver son bouton spécifiquement.
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchPublicPlans(ctrl.signal)
      .then(setPlans)
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        const msg =
          err instanceof Error ? err.message : "Impossible de charger les offres";
        setError(msg);
      });
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setBillingMe(null);
      return;
    }
    let cancelled = false;
    billingApi
      .me()
      .then((data) => {
        if (!cancelled) setBillingMe(data);
      })
      .catch(() => {
        // On reste tolérant : si /me échoue (ex. 403 sur tenant non sélectionné),
        // on fall back sur le CTA "Ouvrir HInvoice" — pas une raison de
        // bloquer l'affichage des plans.
        if (!cancelled) setBillingMe(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  async function handleSubscribe(planSlug: string) {
    setSubscribing(planSlug);
    setSubscribeError(null);
    try {
      const result = await billingApi.start({ plan_slug: planSlug });
      // Redirection vers Ziina. window.location.assign permet à la nav
      // navigateur de fonctionner correctement (history) — `href = ...`
      // serait équivalent. On ne tente pas window.open car les pop-up
      // sont bloqués hors gesture utilisateur direct sur certains navs.
      globalThis.location.assign(result.checkout_url);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "Erreur lors de la souscription";
      setSubscribeError(msg);
      setSubscribing(null);
    }
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <header className="max-w-5xl mx-auto px-6 pt-12 pb-8 flex flex-col items-center text-center">
        <p className="text-xs uppercase tracking-wider text-brand-600 font-semibold">
          HInvoice — édité par Hsasoft
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
          Suivez vos factures, pas vos impayés
        </h1>
        <p className="text-slate-600 mt-4 max-w-2xl">
          OCR automatique de vos PDF, relances par email, liens de paiement
          Ziina. Essayez gratuitement pendant 14 jours, sans carte bancaire.
        </p>
      </header>

      <section className="max-w-5xl mx-auto px-6 pb-16" aria-label="pricing-plans">
        {error && (
          <p
            role="alert"
            className="text-center text-sm text-red-600 bg-red-50 border border-red-200 rounded-md py-3 px-4"
          >
            {error}
          </p>
        )}

        {subscribeError && (
          <p
            role="alert"
            className="text-center text-sm text-red-600 bg-red-50 border border-red-200 rounded-md py-3 px-4 mb-4"
          >
            {subscribeError}
          </p>
        )}

        {!plans && !error && (
          <p className="text-center text-sm text-slate-500 py-12">
            Chargement des offres…
          </p>
        )}

        {plans && plans.length === 0 && !error && (
          <p
            role="status"
            className="text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-md py-6 px-4"
          >
            Aucune offre disponible pour le moment. Revenez bientôt.
          </p>
        )}

        {plans && plans.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                recommended={plan.slug === RECOMMENDED_SLUG}
                isAuthenticated={isAuthenticated}
                currentRole={currentRole}
                billingMe={billingMe}
                subscribing={subscribing}
                onSubscribe={handleSubscribe}
              />
            ))}
          </div>
        )}

        <p className="text-center text-sm text-slate-500 mt-10">
          {isAuthenticated ? (
            <>
              Vous êtes connecté.{" "}
              <Link to="/" className="text-brand-600 hover:underline">
                Retour au dashboard
              </Link>
            </>
          ) : (
            <>
              Déjà client ?{" "}
              <Link to="/login" className="text-brand-600 hover:underline">
                Se connecter
              </Link>
            </>
          )}
        </p>
      </section>
    </main>
  );
}

// ---- PlanCard -----------------------------------------------------------

type CtaState =
  | { kind: "anon"; to: string; label: string }
  | { kind: "open"; to: string; label: string }
  | { kind: "manage"; to: string; label: string }
  | { kind: "subscribe"; planSlug: string; label: string }
  | { kind: "current"; label: string };

function decideCta(args: {
  plan: PlanPublic;
  isAuthenticated: boolean;
  currentRole: UserRole | null;
  billingMe: BillingMeResponse | null;
}): CtaState {
  const { plan, isAuthenticated, currentRole, billingMe } = args;

  if (!isAuthenticated) {
    return {
      kind: "anon",
      to: `/signup?plan=${plan.slug}`,
      label: plan.tier === "free" ? "Créer un compte gratuit" : "Essayer 14 jours",
    };
  }

  // Le plan free n'a jamais de souscription Ziina à déclencher : on garde
  // le CTA "Ouvrir HInvoice" pour ne pas semer la confusion (souscrire
  // à du gratuit n'a pas de sens).
  if (plan.tier === "free") {
    return { kind: "open", to: "/", label: "Ouvrir HInvoice" };
  }

  // Non-admin connecté : on n'expose pas l'action commerciale (engagement
  // financier réservé aux admins). Le bouton renvoie au dashboard.
  if (currentRole !== "admin") {
    return { kind: "open", to: "/", label: "Ouvrir HInvoice" };
  }

  // Admin connecté + souscription existante (active/past_due/canceled/incomplete)
  // → on renvoie vers /settings/billing pour gérer (les changements de plan
  // sont hors scope MVP : cancel + resubscribe via le support).
  if (billingMe?.subscription) {
    if (billingMe.subscription.plan_id === plan.id) {
      return { kind: "current", label: "Plan en cours" };
    }
    return {
      kind: "manage",
      to: "/settings/billing",
      label: "Gérer ma facturation",
    };
  }

  // Admin connecté sans souscription (trial / free) sur un plan payant
  // → on déclenche /api/billing/start et on redirige vers Ziina.
  return {
    kind: "subscribe",
    planSlug: plan.slug,
    label: `Souscrire à ${plan.name}`,
  };
}

function PlanCard({
  plan,
  recommended,
  isAuthenticated,
  currentRole,
  billingMe,
  subscribing,
  onSubscribe,
}: {
  plan: PlanPublic;
  recommended: boolean;
  isAuthenticated: boolean;
  currentRole: UserRole | null;
  billingMe: BillingMeResponse | null;
  subscribing: string | null;
  onSubscribe: (slug: string) => void;
}) {
  const cta = decideCta({ plan, isAuthenticated, currentRole, billingMe });
  const baseClass = [
    "block w-full text-center rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50",
    recommended
      ? "bg-brand-600 text-white hover:bg-brand-700"
      : "bg-slate-100 text-slate-900 hover:bg-slate-200",
  ].join(" ");

  return (
    <article
      aria-label={`plan-${plan.slug}`}
      className={[
        "bg-white rounded-2xl border p-6 flex flex-col",
        recommended
          ? "border-brand-600 shadow-md ring-1 ring-brand-600/20"
          : "border-slate-200 shadow-sm",
      ].join(" ")}
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-slate-900">{plan.name}</h2>
        {recommended && (
          <span className="text-[10px] uppercase tracking-wider bg-brand-600 text-white rounded-full px-2 py-0.5 font-semibold">
            Recommandé
          </span>
        )}
      </div>

      <p className="text-slate-500 text-sm min-h-[2.5rem]">
        {plan.description ?? ""}
      </p>

      <div className="mt-4 mb-6">
        <span className="text-3xl font-bold text-slate-900">
          {plan.price_monthly_cents === 0
            ? "0 €"
            : formatPrice(plan.price_monthly_cents, plan.currency)}
        </span>
        <span className="text-slate-500 text-sm ml-1">/ mois HT</span>
      </div>

      <ul className="space-y-2 text-sm text-slate-700 mb-6 flex-1">
        <li className="flex items-start gap-2">
          <CheckIcon />
          <span>{formatQuota(plan.invoice_quota_monthly)}</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckIcon />
          <span>{formatSeats(plan.seats_max)}</span>
        </li>
        {FEATURE_LABELS.map(({ key, label }) => {
          const enabled = plan.features[key] === true;
          return (
            <li
              key={key}
              className={[
                "flex items-start gap-2",
                enabled ? "text-slate-700" : "text-slate-400 line-through",
              ].join(" ")}
            >
              {enabled ? <CheckIcon /> : <DashIcon />}
              <span>{label}</span>
            </li>
          );
        })}
      </ul>

      {(cta.kind === "anon" ||
        cta.kind === "open" ||
        cta.kind === "manage") && (
        <Link to={cta.to} className={baseClass}>
          {cta.label}
        </Link>
      )}

      {cta.kind === "subscribe" && (
        <button
          type="button"
          onClick={() => onSubscribe(cta.planSlug)}
          disabled={subscribing !== null}
          className={baseClass}
        >
          {subscribing === cta.planSlug ? "Redirection…" : cta.label}
        </button>
      )}

      {cta.kind === "current" && (
        <button
          type="button"
          disabled
          className={baseClass}
          aria-disabled="true"
        >
          {cta.label}
        </button>
      )}
    </article>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="h-4 w-4 text-success shrink-0 mt-0.5"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 111.4-1.4L9 11.58l6.3-6.3a1 1 0 011.4 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function DashIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="h-4 w-4 text-slate-300 shrink-0 mt-0.5"
      fill="currentColor"
    >
      <path d="M5 10a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1z" />
    </svg>
  );
}
